'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { appendSearchParamsToUrl, removeSearchParamsByPrefix } from '@bridge/utils';

import { isPaginationCursorParam } from '../utils/pagination-params';

import type { Pagination, TablePagination } from '../data-table/types';

interface PaginationConfig<T> {
  /** Extract next page parameters from the response */
  getNextPageParams: (response: T) => Record<string, string> | null;
  /** Extract previous page parameters from the response */
  getPrevPageParams?: (response: T) => Record<string, string> | null;
}

function useGenericSearchParamPagination<T extends object>(response: T, config: PaginationConfig<T>): TablePagination {
  const pathname = usePathname() ?? '/';
  const searchParams = useSearchParams();
  const hasPaginationParams = searchParams
    ? Array.from(searchParams.keys()).some(isPaginationCursorParam)
    : false;
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const url = `${baseUrl}${pathname}${searchParams?.toString() ? `?${searchParams.toString()}` : ''}`;

  const nextPageParams = config.getNextPageParams(response);
  const prevPageParams = config.getPrevPageParams?.(response);

  const nextPageHref = nextPageParams ? appendSearchParamsToUrl(url, nextPageParams, baseUrl) : null;
  const prevPageHref = prevPageParams ? appendSearchParamsToUrl(url, prevPageParams, baseUrl) : null;

  const onNextPage = () => {
    if (nextPageHref) {
      const cleanUrl = removeSearchParamsByPrefix(nextPageHref, 'prevCursor', baseUrl);
      window.history.pushState(null, '', cleanUrl);
    }
  };

  const onPrevPage = () => {
    if (prevPageHref) {
      // API provides previous cursor, use it
      // Remove any existing cursor parameters before navigating
      const cleanUrl = removeSearchParamsByPrefix(prevPageHref, 'nextCursor', baseUrl);
      window.history.pushState(null, '', cleanUrl);
    } else {
      // No API cursor available, use history navigation
      if (hasPaginationParams) {
        window.history.back();
      } else {
        // No history, go to first page (pathname without search params)
        window.history.pushState(null, '', pathname || '/');
      }
    }
  };

  return {
    onNextPage,
    onPrevPage,
    hasNextPage: nextPageParams !== null,
    hasPrevPage: prevPageParams !== null || hasPaginationParams,
    ...('count' in response && { count: response.count as number }),
    ...('totalCount' in response && { totalCount: response.totalCount as number }),
  };
}

// helper wrapper to pull off next and previous cursors from APIResponseCursors
export default function useSearchParamPagination(pagination: Pagination): TablePagination {
  return useGenericSearchParamPagination(pagination, {
    getNextPageParams: (pagination) =>
      pagination.cursors.nextCursor ? { nextCursor: pagination.cursors.nextCursor } : null,
    getPrevPageParams: (pagination) =>
      pagination.cursors.prevCursor ? { prevCursor: pagination.cursors.prevCursor } : null,
  });
}

export { useGenericSearchParamPagination };
